import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles, FormControl, InputLabel, Select, MenuItem, Box, Typography, Paper } from '@material-ui/core'
import AdminCommonListLayout from './AdminCommonListLayout'
import { adminSalesList } from '../actions/admin_sales'
import { countryList } from '../actions/admin_countries'

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    width: '100%',
    justifyContent: 'flex-start',
  },
  formControl: {
    minWidth: 120,
  },
  totalsContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
  },
  totalBox: {
    padding: '12px 20px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
  totalLabel: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '4px',
  },
  totalValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
  },
}))

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]

const currentYear = new Date().getFullYear()
const YEARS = []
for (let year = currentYear; year >= 2020; year--) {
  YEARS.push(year)
}

export const AdminSales = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const is_loading = useSelector(() => adminSalesList.isLoading())
  const countries = useSelector(() => countryList.getVisibleObjects())

  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [totals, setTotals] = useState({ total_photographer_amount: 0, total_revenue: 0 })

  // Sort countries alphabetically by name
  const sortedCountries = [...countries].sort((a, b) =>
    (a.country_name || '').localeCompare(b.country_name || '')
  )

  // Set Australia as default when countries load
  useEffect(() => {
    if (countries.length > 0 && selectedCountry === null) {
      const australia = countries.find(c => c.country_name === 'Australia')
      if (australia) {
        setSelectedCountry(australia.id)
      } else if (sortedCountries.length > 0) {
        setSelectedCountry(sortedCountries[0].id)
      }
    }
  }, [countries])

  const columns = [
    {
      field: 'photographer_id',
      title: 'Photographer ID',
    },
    {
      field: 'photographer_name',
      title: 'Photographer Name',
    },
    {
      field: 'photographer_amount',
      title: 'Photographer Amount',
      render: (item) => {
        return item.photographer_amount?.toFixed(2) || '0.00'
      }
    },
    {
      field: 'revenue',
      title: 'Revenue',
      render: (item) => {
        return item.revenue?.toFixed(2) || '0.00'
      }
    },
  ]

  useEffect(() => {
    dispatch(countryList.fetchListIfNeeded())
  }, [])

  useEffect(() => {
    const filter = { year: selectedYear, month: selectedMonth }
    if (selectedCountry) {
      filter.country_id = selectedCountry
    }
    dispatch(adminSalesList.updateListFilter(filter))
    dispatch(adminSalesList.fetchListIfNeeded(true)).then(() => {
      setTotals(adminSalesList.getTotals())
    })
  }, [selectedYear, selectedMonth, selectedCountry])

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value)
  }

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value)
  }

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value)
  }

  const renderFilters = () => {
    return (
      <div>
        <div className={classes.filterContainer}>
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              label="Year"
            >
              {YEARS.map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              onChange={handleMonthChange}
              label="Month"
            >
              {MONTHS.map((month) => (
                <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel>Country</InputLabel>
            <Select
              value={selectedCountry || ''}
              onChange={handleCountryChange}
              label="Country"
            >
              {sortedCountries.map((country) => (
                <MenuItem key={country.id} value={country.id}>{country.country_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className={classes.totalsContainer}>
          <div className={classes.totalBox}>
            <div className={classes.totalLabel}>Total Photographer Amount</div>
            <div className={classes.totalValue}>{totals.total_photographer_amount?.toFixed(2) || '0.00'}</div>
          </div>
          <div className={classes.totalBox}>
            <div className={classes.totalLabel}>Total Revenue</div>
            <div className={classes.totalValue}>{totals.total_revenue?.toFixed(2) || '0.00'}</div>
          </div>
        </div>
      </div>
    )
  }

  const selectedMonthName = MONTHS.find(m => m.value === selectedMonth)?.label || ''

  return (
    <AdminCommonListLayout
      active_key='sales'
      title={`Sales - ${selectedMonthName} ${selectedYear}`}
      breadcrumbs={[
        { name: 'admin_home' },
        {
          name: 'sales',
          label: 'Sales',
          url: '/admin/sales',
        },
      ]}
      columns={columns}
      is_loading={is_loading}
      item_list={adminSalesList}
      renderAdditionalActions={renderFilters}
    />
  )
}
